import {
  Button,
  CardContainer,
  Typography,
  ReactRouter,
  LucideIcons,
  HeroCard,
} from '@e-burgos/tucu-ui';
import { ROUTES } from '../router/routes-config';
import { APP_URLS, navigateBetweenApps } from '@e-burgos-mfe/utils';

const InitialPage = () => {
  const navigate = ReactRouter.useNavigate();

  const features = [
    {
      icon: <LucideIcons.Layers className="w-8 h-8" />,
      title: 'Path-based Routing',
      description:
        'Each app is deployed independently on its own path, enabling true micro-frontend architecture.',
    },
    {
      icon: <LucideIcons.Network className="w-8 h-8" />,
      title: 'Unified Orchestration',
      description:
        'All apps share a unified orchestrator through ShellWrapper, ensuring consistent integration.',
    },
    {
      icon: <LucideIcons.Shield className="w-8 h-8" />,
      title: 'Route Protection',
      description:
        'Built-in support for public/private routes with automatic authentication handling.',
    },
    {
      icon: <LucideIcons.Navigation className="w-8 h-8" />,
      title: 'Smart Navigation',
      description:
        'Automatic detection of in-app vs inter-app navigation for seamless user experience.',
    },
    {
      icon: <LucideIcons.Code className="w-8 h-8" />,
      title: 'Type-Safe',
      description:
        'Full TypeScript support with discriminated unions ensuring correct MFE mode configuration.',
    },
    {
      icon: <LucideIcons.Rocket className="w-8 h-8" />,
      title: 'Independent Deployment',
      description:
        'Each app can be developed, built, and deployed separately without affecting others.',
    },
  ];

  const apps = [
    {
      name: 'Authentication',
      description:
        'Complete authentication flow with login, sign-up, password reset, and phone verification',
      icon: <LucideIcons.Lock className="w-6 h-6" />,
      url: APP_URLS.AUTHENTICATION,
      color: 'from-blue-500 to-indigo-600',
    },
    {
      name: 'Dashboard',
      description: 'Admin dashboard with sidebar layout and protected routes',
      icon: <LucideIcons.LayoutDashboard className="w-6 h-6" />,
      url: APP_URLS.DASHBOARD,
      color: 'from-purple-500 to-pink-600',
    },
    {
      name: 'User Profile',
      description: 'User profile management with horizontal navigation layout',
      icon: <LucideIcons.User className="w-6 h-6" />,
      url: APP_URLS.USER_PROFILE,
      color: 'from-green-500 to-emerald-600',
    },
  ];

  const documentationLinks = [
    {
      title: 'Integration Guide',
      description:
        'Complete step-by-step guide for integrating a new micro-frontend',
      icon: <LucideIcons.BookOpen className="w-5 h-5" />,
      href: ROUTES.IntegrationGuide,
    },
    {
      title: 'Architecture Overview',
      description: 'Understand how MFE architecture works with Tucu-UI',
      icon: <LucideIcons.Building className="w-5 h-5" />,
      href: ROUTES.ArchitectureOverview,
    },
    {
      title: 'Development Guide',
      description: 'Development workflow, dev server, and environment setup',
      icon: <LucideIcons.Terminal className="w-5 h-5" />,
      href: ROUTES.DevelopmentGuide,
    },
    {
      title: 'GitHub Pages Deployment',
      description:
        'Complete guide for deploying all apps to GitHub Pages using GitHub Actions',
      icon: <LucideIcons.Rocket className="w-5 h-5" />,
      href: ROUTES.DeploymentGitHubPages,
    },
  ];

  return (
    <div className="w-full min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden -mt-5!">
        <HeroCard
          title="Micro-Frontend Architecture"
          description="Built with @e-burgos/tucu-ui and designed for scalable, independent deployments"
          icon={
            <div className="inline-flex items-center justify-center w-20 h-20 mb-6 bg-linear-to-br from-brand to-indigo-600 rounded-2xl shadow-lg">
              <LucideIcons.Layers className="w-10 h-10 text-white" />
            </div>
          }
          content={
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                variant="solid"
                color="primary"
                size="large"
                onClick={() => navigate(ROUTES.IntegrationGuide)}
                className="min-w-[200px]"
              >
                <LucideIcons.Rocket className="w-5 h-5 mr-2" />
                Get Started
              </Button>
              <Button
                variant="ghost"
                color="primary"
                size="large"
                onClick={() => navigate(ROUTES.ArchitectureOverview)}
                className="min-w-[200px]"
              >
                <LucideIcons.BookOpen className="w-5 h-5 mr-2" />
                Learn More
              </Button>
            </div>
          }
        />
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Typography
              tag="h2"
              className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4"
            >
              Key Features
            </Typography>
            <Typography
              tag="p"
              className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto"
            >
              Everything you need to build and deploy micro-frontends with
              Tucu-UI
            </Typography>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <CardContainer
                key={index}
                className="p-6 hover:shadow-lg transition-all duration-300 border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center justify-center w-14 h-14 mb-4 bg-linear-to-br from-brand/10 to-indigo-100 dark:from-brand/20 dark:to-indigo-900/30 rounded-xl">
                  <div className="text-brand dark:text-indigo-400">
                    {feature.icon}
                  </div>
                </div>
                <Typography
                  tag="h3"
                  className="text-xl font-semibold text-gray-900 dark:text-white mb-2"
                >
                  {feature.title}
                </Typography>
                <Typography
                  tag="p"
                  className="text-gray-600 dark:text-gray-400"
                >
                  {feature.description}
                </Typography>
              </CardContainer>
            ))}
          </div>
        </div>
      </section>

      {/* Architecture Overview Section */}
      <section className="py-20 lg:py-24 bg-gray-100 bg-light-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Typography
              tag="h2"
              className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4"
            >
              Architecture Overview
            </Typography>
            <Typography
              tag="p"
              className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto"
            >
              Path-based Micro-Frontend architecture with unified orchestration
            </Typography>
          </div>

          <CardContainer className="p-8 lg:p-12 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <Typography
                  tag="h3"
                  className="text-2xl font-bold text-gray-900 dark:text-white mb-4"
                >
                  How It Works
                </Typography>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="shrink-0 w-6 h-6 rounded-full bg-brand/10 dark:bg-brand/20 flex items-center justify-center mt-0.5">
                      <LucideIcons.Check className="w-4 h-4 text-brand" />
                    </div>
                    <div>
                      <Typography
                        tag="p"
                        className="font-semibold text-gray-900 dark:text-white"
                      >
                        Path-based Routing
                      </Typography>
                      <Typography
                        tag="p"
                        className="text-gray-600 dark:text-gray-400 text-sm"
                      >
                        Each app deployed on its own path (/authentication,
                        /landing, etc. in local dev; /repo-name/app-name in GitHub Pages)
                      </Typography>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="shrink-0 w-6 h-6 rounded-full bg-brand/10 dark:bg-brand/20 flex items-center justify-center mt-0.5">
                      <LucideIcons.Check className="w-4 h-4 text-brand" />
                    </div>
                    <div>
                      <Typography
                        tag="p"
                        className="font-semibold text-gray-900 dark:text-white"
                      >
                        Unified Orchestration
                      </Typography>
                      <Typography
                        tag="p"
                        className="text-gray-600 dark:text-gray-400 text-sm"
                      >
                        ShellWrapper provides consistent integration across all
                        apps
                      </Typography>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="shrink-0 w-6 h-6 rounded-full bg-brand/10 dark:bg-brand/20 flex items-center justify-center mt-0.5">
                      <LucideIcons.Check className="w-4 h-4 text-brand" />
                    </div>
                    <div>
                      <Typography
                        tag="p"
                        className="font-semibold text-gray-900 dark:text-white"
                      >
                        Tucu-UI MFE Support
                      </Typography>
                      <Typography
                        tag="p"
                        className="text-gray-600 dark:text-gray-400 text-sm"
                      >
                        Built-in MFE mode in ThemeProvider via discriminated
                        unions
                      </Typography>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="shrink-0 w-6 h-6 rounded-full bg-brand/10 dark:bg-brand/20 flex items-center justify-center mt-0.5">
                      <LucideIcons.Check className="w-4 h-4 text-brand" />
                    </div>
                    <div>
                      <Typography
                        tag="p"
                        className="font-semibold text-gray-900 dark:text-white"
                      >
                        Smart Navigation
                      </Typography>
                      <Typography
                        tag="p"
                        className="text-gray-600 dark:text-gray-400 text-sm"
                      >
                        Automatic detection of in-app vs inter-app navigation
                      </Typography>
                    </div>
                  </li>
                </ul>
              </div>
              <div className="relative">
                <div className="bg-linear-to-br from-brand/5 to-indigo-500/10 dark:from-brand/10 dark:to-indigo-900/20 rounded-2xl p-8 border border-brand/20 dark:border-brand/30">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-brand"></div>
                      <Typography
                        tag="p"
                        className="text-sm font-mono text-gray-700 dark:text-gray-300"
                      >
                        Unified Dev Server (Port 3000)
                      </Typography>
                    </div>
                    <div className="pl-6 space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                        <Typography
                          tag="p"
                          className="text-sm font-mono text-gray-600 dark:text-gray-400"
                        >
                          /authentication → App: Auth
                        </Typography>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                        <Typography
                          tag="p"
                          className="text-sm font-mono text-gray-600 dark:text-gray-400"
                        >
                          /landing → App: Landing
                        </Typography>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <Typography
                          tag="p"
                          className="text-sm font-mono text-gray-600 dark:text-gray-400"
                        >
                          /dashboard → App: Dashboard
                        </Typography>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        <LucideIcons.Package className="w-4 h-4 text-gray-500" />
                        <Typography
                          tag="p"
                          className="text-sm text-gray-600 dark:text-gray-400"
                        >
                          Shared Libraries: shell, api, auth-security, utils
                        </Typography>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContainer>
        </div>
      </section>

      {/* Example Apps Section */}
      <section className="py-20 lg:py-24 ">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Typography
              tag="h2"
              className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4"
            >
              Example Applications
            </Typography>
            <Typography
              tag="p"
              className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto"
            >
              Explore our micro-frontend implementations
            </Typography>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {apps.map((app, index) => (
              <div
                key={index}
                onClick={() => navigateBetweenApps(app.url)}
                className="cursor-pointer"
              >
                <CardContainer className="p-6 hover:shadow-lg transition-all duration-300 border border-gray-200 dark:border-gray-700 group">
                  <div
                    className={`w-12 h-12 rounded-xl bg-linear-to-br ${app.color} flex items-center justify-center mb-4 text-white shadow-lg group-hover:scale-110 transition-transform`}
                  >
                    {app.icon}
                  </div>
                  <Typography
                    tag="h3"
                    className="text-xl font-semibold text-gray-900 dark:text-white mb-2"
                  >
                    {app.name}
                  </Typography>
                  <Typography
                    tag="p"
                    className="text-gray-600 dark:text-gray-400 mb-4"
                  >
                    {app.description}
                  </Typography>
                  <Button
                    variant="ghost"
                    color="primary"
                    size="small"
                    className="w-full group-hover:bg-brand/10"
                  >
                    Explore App
                    <LucideIcons.ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContainer>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to test the implementation */}
      <section className="py-20 lg:py-24 bg-gray-100 bg-light-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Typography
              tag="h2"
              className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4"
            >
              How to Test the Implementation
            </Typography>
            <Typography
              tag="p"
              className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto"
            >
              Step-by-step guide to try login, navigation, and protected routes
            </Typography>
          </div>

          <CardContainer className="p-8 lg:p-12 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
            <div className="space-y-8">
              <div>
                <Typography
                  tag="h3"
                  className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
                >
                  <LucideIcons.ListOrdered className="w-5 h-5 text-brand" />
                  Step-by-step: Login and register
                </Typography>
                <ol className="list-decimal list-inside space-y-3 text-gray-600 dark:text-gray-400">
                  <li>
                    In the header, open the <strong className="text-gray-900 dark:text-white">Options</strong> menu (or the user icon when logged in).
                  </li>
                  <li>
                    Click <strong className="text-gray-900 dark:text-white">Login</strong> to go to the Authentication app.
                  </li>
                  <li>
                    On the auth app you can <strong className="text-gray-900 dark:text-white">Log in</strong> or <strong className="text-gray-900 dark:text-white">Sign up</strong> (Register). Use any email and password; all data is mock. <strong className="text-gray-900 dark:text-white">If you have not registered an account yet, you must do so first</strong> before you can log in.
                  </li>
                  <li>
                    After logging in, you are redirected to the Landing page. From <strong className="text-gray-900 dark:text-white">Options</strong> you can open <strong className="text-gray-900 dark:text-white">Profile</strong> or <strong className="text-gray-900 dark:text-white">Logout</strong>.
                  </li>
                </ol>
              </div>

              <div>
                <Typography
                  tag="h3"
                  className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
                >
                  <LucideIcons.Database className="w-5 h-5 text-brand" />
                  Fictitious data and shared state
                </Typography>
                <Typography
                  tag="p"
                  className="text-gray-600 dark:text-gray-400 mb-2"
                >
                  All login and user data in this demo is <strong className="text-gray-900 dark:text-white">mock</strong>. It is stored in the browser&apos;s <strong className="text-gray-900 dark:text-white">localStorage</strong> under the key <code className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-sm">demo-auth-storage</code>. This serves as an example of sharing state between micro-frontends: the auth store is implemented with <strong className="text-gray-900 dark:text-white">Zustand</strong> and persisted to localStorage, so every app (Landing, Dashboard, User Profile, Authentication) reads the same auth state.
                </Typography>
              </div>

              <div>
                <Typography
                  tag="h3"
                  className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
                >
                  <LucideIcons.Navigation className="w-5 h-5 text-brand" />
                  Navigating between apps
                </Typography>
                <Typography
                  tag="p"
                  className="text-gray-600 dark:text-gray-400 mb-3"
                >
                  You can move between apps from the <strong className="text-gray-900 dark:text-white">Example Applications</strong> cards above (Authentication, Dashboard, User Profile) or from the header menu. Each app is loaded on its own path; switching apps triggers a full navigation (e.g. <code className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-sm">/dashboard</code>, <code className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-sm">/user-profile</code>).
                </Typography>
              </div>

              <div>
                <Typography
                  tag="h3"
                  className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2"
                >
                  <LucideIcons.Shield className="w-5 h-5 text-brand" />
                  Protected routes (Dashboard and User Profile)
                </Typography>
                <Typography
                  tag="p"
                  className="text-gray-600 dark:text-gray-400 mb-3"
                >
                  Dashboard and User Profile use <strong className="text-gray-900 dark:text-white">protected routes</strong>: their routes are configured with <code className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-sm">isPublic: false</code>. If you open Dashboard or User Profile <strong className="text-gray-900 dark:text-white">without being logged in</strong>, Tucu-UI handles it as follows:
                </Typography>
                <ul className="space-y-2 text-gray-600 dark:text-gray-400 list-disc list-inside">
                  <li>
                    <strong className="text-gray-900 dark:text-white">ShellWrapper</strong> receives <code className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-sm">isAuthenticated</code> from the Zustand auth store and <code className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-sm">loginUrl</code> (Authentication app).
                  </li>
                  <li>
                    Private routes are wrapped with an <strong className="text-gray-900 dark:text-white">AuthProvider</strong>. When <code className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-sm">isAuthenticated === false</code>, the provider renders an <strong className="text-gray-900 dark:text-white">AccessDeniedPage</strong>.
                  </li>
                  <li>
                    <strong className="text-gray-900 dark:text-white">AccessDeniedPage</strong> redirects the user to the login app via <code className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-sm">window.location.href</code>, so unauthenticated users are sent to Authentication to log in before accessing Dashboard or User Profile.
                  </li>
                </ul>
                <Typography
                  tag="p"
                  className="text-gray-600 dark:text-gray-400 mt-3"
                >
                  To try it: log out (or use a private window), then click on Dashboard or User Profile from the Example Applications section; you should be redirected to the Authentication app.
                </Typography>
              </div>
            </div>
          </CardContainer>
        </div>
      </section>

      {/* Documentation Section */}
      <section className="py-20 lg:py-24 bg-light-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Typography
              tag="h2"
              className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4"
            >
              Documentation
            </Typography>
            <Typography
              tag="p"
              className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto"
            >
              Comprehensive guides to help you build with Micro-Frontends
            </Typography>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {documentationLinks.map((doc, index) => (
              <div
                key={index}
                onClick={() => navigate(doc.href)}
                className="cursor-pointer"
              >
                <CardContainer className="p-6 hover:shadow-lg transition-all duration-300 border border-gray-200 dark:border-gray-700 group bg-white dark:bg-dark">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-brand/10 dark:bg-brand/20 flex items-center justify-center text-brand group-hover:bg-brand/20 dark:group-hover:bg-brand/30 transition-colors">
                      {doc.icon}
                    </div>
                    <Typography
                      tag="h3"
                      className="text-lg font-semibold text-gray-900 dark:text-white"
                    >
                      {doc.title}
                    </Typography>
                  </div>
                  <Typography
                    tag="p"
                    className="text-gray-600 dark:text-gray-400 text-sm mb-4"
                  >
                    {doc.description}
                  </Typography>
                  <div className="flex items-center text-brand text-sm font-medium group-hover:gap-2 transition-all">
                    Read More
                    <LucideIcons.ArrowRight className="w-4 h-4 ml-1 group-hover:ml-2 transition-all" />
                  </div>
                </CardContainer>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <CardContainer className="p-8 lg:p-12 flex flex-col items-center justify-center">
            <div className="flex items-center justify-center w-16 h-16 mb-6 bg-linear-to-br from-brand to-indigo-600 rounded-2xl shadow-lg">
              <LucideIcons.Rocket className="w-8 h-8 text-white" />
            </div>
            <Typography
              tag="h2"
              className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4"
            >
              Ready to Build?
            </Typography>
            <Typography
              tag="p"
              className="text-lg text-gray-600 dark:text-gray-400 mb-8"
            >
              Start building your micro-frontend architecture with Tucu-UI today
            </Typography>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="solid"
                color="primary"
                size="large"
                onClick={() => navigate(ROUTES.IntegrationGuide)}
              >
                <LucideIcons.BookOpen className="w-5 h-5 mr-2" />
                Read Integration Guide
              </Button>
              <Button
                variant="ghost"
                color="primary"
                size="large"
                onClick={() => navigateBetweenApps(APP_URLS.AUTHENTICATION)}
              >
                <LucideIcons.ExternalLink className="w-5 h-5 mr-2" />
                View Example Apps
              </Button>
            </div>
          </CardContainer>
        </div>
      </section>
    </div>
  );
};

export default InitialPage;
